'use client';

import { Check, ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { useLocale } from '@/components/common/locale-provider';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useTranslations } from '@/hooks/use-translations';
import type { CountryOption } from '@/lib/auth/iso-countries';
import { cn } from '@/lib/utils';

type CountryComboboxProps = {
  id: string;
  value: string;
  onValueChange: (value: string) => void;
  options: CountryOption[];
  placeholder?: string;
  'aria-label'?: string;
  disabled?: boolean;
};

export function CountryCombobox({
  id,
  value,
  onValueChange,
  options,
  placeholder,
  'aria-label': ariaLabel,
  disabled = false,
}: CountryComboboxProps) {
  const { locale } = useLocale();
  const { t } = useTranslations(locale);
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState(0);
  const selected = options.find((o) => o.code === value);
  const resolvedPlaceholder =
    placeholder ?? t('auth.signupFlow.countryPlaceholder');
  const resolvedAriaLabel = ariaLabel ?? t('auth.signupFlow.countryLabel');

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) {
          setSession((s) => s + 1);
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={resolvedAriaLabel}
          disabled={disabled}
          className={cn(
            'h-10 w-full justify-between px-3 font-normal',
            !selected && 'text-muted-foreground',
          )}
        >
          <span className="truncate">
            {selected ? selected.name : resolvedPlaceholder}
          </span>
          <ChevronDown className="size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[min(100vw-2rem,24rem)] p-0" align="start">
        <Command key={session} shouldFilter>
          <CommandInput
            placeholder={t('auth.signupFlow.countrySearchPlaceholder')}
          />
          <CommandList>
            <CommandEmpty>{t('auth.signupFlow.countryEmpty')}</CommandEmpty>
            <CommandGroup>
              {options.map(({ code, name }) => (
                <CommandItem
                  key={code}
                  value={`${name} ${code}`}
                  onSelect={() => {
                    onValueChange(code);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 size-4 shrink-0',
                      value === code ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  {name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
